import React, { useState, useEffect } from 'react';
import api from '../utils/axiosConfig';
import './Companies.css';

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  
  // Form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [newCompany, setNewCompany] = useState({ name: '', cnpj: '' });
  const [selectedUser, setSelectedUser] = useState({ userId: '', companyId: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [companiesRes, usersRes] = await Promise.all([
        api.get('/companies'),
        api.get('/auth/users')
      ]);
      
      setCompanies(companiesRes.data);
      setUsers(usersRes.data);
      setError('');
    } catch (err) {
      setError('Erro ao carregar dados: ' + err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    try {
      await api.post('/companies', newCompany);
      setMessage('Empresa cadastrada com sucesso!');
      setNewCompany({ name: '', cnpj: '' });
      setShowAddForm(false);
      fetchData();
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError('Erro ao cadastrar empresa: ' + err.response?.data?.message || err.message);
    }
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setNewCompany({ name: company.name, cnpj: company.cnpj });
    setShowEditForm(true);
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/companies/${editingCompany._id}`, newCompany);
      setMessage('Empresa atualizada com sucesso!');
      setNewCompany({ name: '', cnpj: '' });
      setEditingCompany(null);
      setShowEditForm(false);
      fetchData();
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError('Erro ao atualizar empresa: ' + err.response?.data?.message || err.message);
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (window.confirm('Tem certeza que deseja excluir esta empresa?')) {
      try {
        await api.delete(`/companies/${companyId}`);
        setMessage('Empresa excluída com sucesso!');
        fetchData();
        
        // Clear message after 3 seconds
        setTimeout(() => setMessage(''), 3000);
      } catch (err) {
        setError('Erro ao excluir empresa: ' + err.response?.data?.message || err.message);
      }
    }
  };

  const handleAddUserToCompany = async (e) => {
    e.preventDefault();
    try {
      await api.post('/companies/user/add', selectedUser);
      setMessage('Usuário adicionado à empresa com sucesso!');
      setSelectedUser({ userId: '', companyId: '' });
      fetchData();
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError('Erro ao adicionar usuário à empresa: ' + err.response?.data?.message || err.message);
    }
  };

  const handleRemoveUserFromCompany = async (userId, companyId) => {
    if (window.confirm('Tem certeza que deseja remover este usuário da empresa?')) {
      try {
        await api.post('/companies/user/remove', { userId, companyId });
        setMessage('Usuário removido da empresa com sucesso!');
        fetchData();
        
        // Clear message after 3 seconds
        setTimeout(() => setMessage(''), 3000);
      } catch (err) {
        setError('Erro ao remover usuário da empresa: ' + err.response?.data?.message || err.message);
      }
    }
  };

  if (loading) return <div className="loading">Carregando...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="companies-container">
      <h2>Gerenciamento de Empresas</h2>
      
      {message && <div className="success-message">{message}</div>}
      {error && <div className="error-message">{error}</div>}
      
      <div className="companies-actions">
        <button 
          className="add-company-button"
          onClick={() => {
            setShowAddForm(!showAddForm);
            setShowEditForm(false);
            setEditingCompany(null);
            setNewCompany({ name: '', cnpj: '' });
          }}
        >
          {showAddForm ? 'Cancelar' : 'Adicionar Empresa'}
        </button>
      </div>
      
      {(showAddForm || showEditForm) && (
        <div className="add-company-form">
          <h3>{showEditForm ? 'Editar Empresa' : 'Adicionar Nova Empresa'}</h3>
          <form onSubmit={showEditForm ? handleUpdateCompany : handleAddCompany}>
            <div className="form-group">
              <label htmlFor="name">Nome da Empresa:</label>
              <input
                type="text"
                id="name"
                value={newCompany.name}
                onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="cnpj">CNPJ:</label>
              <input
                type="text"
                id="cnpj"
                value={newCompany.cnpj}
                onChange={(e) => setNewCompany({...newCompany, cnpj: e.target.value})}
                required
              />
            </div>
            
            <div className="form-buttons">
              <button type="submit" className="submit-button">
                {showEditForm ? 'Atualizar Empresa' : 'Cadastrar Empresa'}
              </button>
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => {
                  setShowAddForm(false);
                  setShowEditForm(false);
                  setEditingCompany(null);
                  setNewCompany({ name: '', cnpj: '' });
                }}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}
      
      <div className="companies-list">
        <h3>Empresas Cadastradas</h3>
        {companies.length === 0 ? (
          <p>Nenhuma empresa cadastrada.</p>
        ) : (
          <table className="companies-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>CNPJ</th>
                <th>Usuários</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {companies.map(company => (
                <tr key={company._id}>
                  <td>{company.name}</td>
                  <td>{company.cnpj}</td>
                  <td>
                    <div className="company-users">
                      {users.filter(user => 
                        user.companies?.some(comp => 
                          typeof comp === 'object' ? comp._id === company._id : comp === company._id
                        )
                      ).map(user => (
                        <div key={`${user._id}-${company._id}`} className="user-item">
                          <span>{user.username} ({user.email})</span>
                          <button 
                            className="remove-user-button"
                            onClick={() => handleRemoveUserFromCompany(user._id, company._id)}
                          >
                            Remover
                          </button>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td>
                    <div className="company-actions">
                      <button 
                        className="edit-button"
                        onClick={() => handleEditCompany(company)}
                      >
                        Editar
                      </button>
                      <button 
                        className="delete-button"
                        onClick={() => handleDeleteCompany(company._id)}
                        disabled={company._id === '1'} // Prevent deleting default company
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      <div className="user-company-management">
        <h3>Gerenciar Usuários nas Empresas</h3>
        <form onSubmit={handleAddUserToCompany} className="add-user-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="userId">Usuário:</label>
              <select
                id="userId"
                value={selectedUser.userId}
                onChange={(e) => setSelectedUser({...selectedUser, userId: e.target.value})}
                required
              >
                <option value="">Selecione um usuário</option>
                {users.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.username} ({user.email})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="companyId">Empresa:</label>
              <select
                id="companyId"
                value={selectedUser.companyId}
                onChange={(e) => setSelectedUser({...selectedUser, companyId: e.target.value})}
                required
              >
                <option value="">Selecione uma empresa</option>
                {companies.map(company => (
                  <option key={company._id} value={company._id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <button type="submit" className="submit-button">Adicionar Usuário à Empresa</button>
        </form>
      </div>
    </div>
  );
};

export default Companies;